import {
  BufferGeometry,
  Euler,
  Material,
  Camera,
  Quaternion,
  Scene,
  Vector3,
} from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { Pass } from "three/examples/jsm/postprocessing/Pass";

import { EngineRequest } from "./EngineRequest";
import { EngineResizeController } from "./EngineResizeController";
import { CSS2DRenderer } from "./renderer/CSS2DRenderer";
import { CSS3DRenderer } from "./renderer/CSS3DRenderer.js";

import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { IRenderer } from "./renderer/IRenderer";
import { WebGLRendererSingleton } from "./renderer/WebGLRendererSingleton";
import { WebGPURendererSingleton } from "./renderer/WebGPURendererSingleton";

export type EngineRendererBackend = "webgl" | "webgpu";

export type EngineOptions = {
  /**
   * Classic WebGL path for EffectComposer / MRT / onBeforeCompile pipelines
   * (UV radiance, Fake Lumen, etc.). Default is WebGPU.
   */
  rendererBackend?: EngineRendererBackend;
};
import { logger } from "../util/Logger";
import { installAssetTracking } from "../loader/assetTracking";
import { BasicCamera } from "./BasicCamera";
import type { CameraInterface } from "./CameraTypes";
import { Entity, setActiveEngineResolver } from "./Entity";
import { RenderTarget } from "./RenderTarget";
import {
  MaterialGeometryCache,
  MaterialGeometryInvalidationMode,
} from "./MaterialGeometryCache";
import {
  listActiveSceneMaterials,
  logActiveSceneMaterialsToConsole as logActiveSceneMaterialsToConsoleImpl,
  summarizeActiveSceneMaterials,
} from "./ActiveSceneMaterials";
import { RenderSceneSwapCoordinator } from "./RenderSceneSwapCoordinator";
import { SceneMutationObserver } from "./SceneMutationObserver";
import { frameProfiler } from "./FrameProfiler";
import { engineProfiling } from "./EngineProfiling";
import { renderEngineSnapshot } from "./EngineSnapshotService";
import { FramePipeline } from "./runtime/FramePipeline";
import type { FrameContext } from "./runtime/EngineSystem";
import { EntitySystem } from "./systems/EntitySystem";
import { SceneMutationSystem } from "./systems/SceneMutationSystem";
import { RequestQueueSystem } from "./systems/RequestQueueSystem";
import { MaterialGeometryCacheSystem } from "./systems/MaterialGeometryCacheSystem";
import { CssRenderSystem } from "./systems/CssRenderSystem";
import { RenderTargetSystem } from "./systems/RenderTargetSystem";
import { MainRenderSystem } from "./systems/MainRenderSystem";
import { PostProcessingController, type TslEffect } from "./postprocessing/PostProcessingController";
import { InteractionController } from "./interaction/InteractionController";
import { DebugLogController } from "./debug/DebugLogController";
import { installRenderTrace } from "./debug/RenderTrace";
import { EngineGuiController } from "./gui/EngineGuiController";

export {
  MaterialGeometryCache,
  MaterialGeometryInvalidationMode,
} from "./MaterialGeometryCache";
export { BasicCamera } from "./BasicCamera";
export type { CameraInterface } from "./CameraTypes";
export { Entity } from "./Entity";
export { RenderTarget } from "./RenderTarget";
export { registerEvents } from "./registerEvents";
export { RenderSceneReparentManager } from "./RenderSceneReparent";
export { RenderSceneSwapCoordinator } from "./RenderSceneSwapCoordinator";
export {
  listActiveSceneMaterials,
  summarizeActiveSceneMaterials,
  logActiveSceneMaterialsToConsole,
  type ActiveSceneMaterialSummaryRow,
} from "./ActiveSceneMaterials";
export {
  logMaterialGeometryBuckets,
  logSceneToRenderTree,
  logRenderSceneDebugBundle,
} from "./RenderSceneDebugLog";

export class Engine extends EventTarget {
  private _scene: Scene | undefined;
  private _bindElement: HTMLElement | undefined;
  private _start = false;
  private _width: number = 512;
  private _height: number = 512;
  private _requestUpdate = true;

  private _camera?: CameraInterface;

  private _viewAngle = 25;
  private static _activeEngine: Engine;

  /** Materyal başına geometri listesi; sync her kare animate içinde çağrılır. */
  private readonly _materialGeometryCache = new MaterialGeometryCache();
  private _materialGeometryCacheRequestUpdateListening = false;
  private readonly _materialGeometryCacheOnRequestUpdate = () => {
    this._materialGeometryCache.markStale();
  };

  /** WebGL swap + reparent + render-scene debug tek yerde. */
  private readonly _renderSceneSwap = new RenderSceneSwapCoordinator();

  /** Kare-fazlarini kayitli sistemler uzerinden yuruten pipeline. */
  private readonly _pipeline = new FramePipeline();

  /** Entity yasam dongusu (begin/animate/beforeRender + add/remove). */
  private readonly _entitySystem = new EntitySystem();

  /** Render oncesi/sonrasi tek seferlik is kuyruklari. */
  private readonly _requestSystem = new RequestQueueSystem();

  /** Materyal->geometri onbellegini her kare sync eder (cache asagida). */
  private readonly _matGeomSystem = new MaterialGeometryCacheSystem(
    this._materialGeometryCache,
  );

  /** CSS2D/CSS3D katmanlari + offscreen render target'lar. */
  private readonly _cssSystem = new CssRenderSystem();
  private readonly _rtSystem = new RenderTargetSystem();

  /** EffectComposer + pass yonetimi (composer asagidaki render sisteminden once). */
  private readonly _postProcessing = new PostProcessingController(this);
  /** Ana WebGL render gecisi + render override + son-kare webgl istatistikleri. */
  private readonly _mainRenderSystem = new MainRenderSystem(
    this._renderSceneSwap,
    this._materialGeometryCache,
    this._postProcessing,
  );

  /** Pointer/kamera etkilesim durumu + interaction-start/end. */
  private readonly _interaction = new InteractionController(this);
  /** Sahneye gecici hata ayiklama mesh'leri (log/clearDebugLog). */
  private readonly _debugLog = new DebugLogController(this);
  /** lil-gui debug paneli (tembel). */
  private readonly _gui = new EngineGuiController(this);

  static get activeEngine() {
    return Engine._activeEngine;
  }

  private _transformControls?: TransformControls;

  get transformControls() {
    if (!this._transformControls) {
      this._transformControls = new TransformControls(
        this.activeCamera!.instance,
        this.canvas,
      );
      this.activeScene!.add(this._transformControls.getHelper());
    }
    return this._transformControls;
  }

  constructor(bindElement?: HTMLElement, options?: EngineOptions) {
    super();
    // Loader'ları (GLTF/FBX/OBJ/RGBE/KTX2/Texture) bir kez sarmala: Engine'i
    // kullanan HER uygulamada yüklenen asset'ler otomatik AssetRegistry'ye girer
    // (idempotent). scene-mutated'da da aktif sahne track edilir (bkz. animate).
    installAssetTracking();
    setActiveEngineResolver(() => Engine.activeEngine);
    Engine._activeEngine = this;
    this.renderer =
      options?.rendererBackend === "webgl"
        ? WebGLRendererSingleton.getInstance()
        : WebGPURendererSingleton.getInstance();
    this.cssRenderer = new CSS2DRenderer();
    this.cssRenderer3d = new CSS3DRenderer();
    this._resizeController = new EngineResizeController({
      renderer: this.renderer,
      cssRenderer: this.cssRenderer,
      cssRenderer3d: this.cssRenderer3d,
      getBindElement: () => this._bindElement,
      getCamera: () => this._camera,
      getComposer: () => this._postProcessing.composer,
      getSize: () => ({ width: this._width, height: this._height }),
      setSize: (width, height) => {
        this._width = width;
        this._height = height;
      },
      setCssRendererAdded: (value) => {
        this.cssRendererAdded = value;
      },
      setCssRenderer3dAdded: (value) => {
        this.cssRenderer3dAdded = value;
      },
      requestUpdate: () => {
        this._requestUpdate = true;
      },
      dispatchResize: () => this.dispatchCustomEvent("engine-resize"),
    });

    this._scene = new Scene();
    SceneMutationObserver.observe(this._scene, this);

    //this._shadowRoot = shadowRoot;
    if (bindElement) this.bindElement(bindElement);

    this._camera = new BasicCamera(this.width, this.height);

    //camera controllers
    this._camera?.instance.position.setZ(15);
    this._camera?.instance.position.setY(0);

    this.addEventListener("engine-request-before", (event: any) => {
      this.addBeforeRequest(event.detail);
    });

    this.addEventListener("engine-request-after", (event: any) => {
      this.addAfterRequest(event.detail);
    });

    this.addEventListener("request-update", (event: any) => {
      this._requestUpdate = true;
    });

    this.addEventListener("camera-change", (_event: any) => {
      // OrbitControls fires `start` on pointerdown. Hiding CSS there made
      // product bbox meters vanish on the first camera grab (looks like resize).
      this._interaction.setCameraActive(true);
    });
    this.addEventListener("camera-end", (_event: any) => {
      this._interaction.setCameraActive(false);
    });

    this._initFramePipeline();
    installRenderTrace(this);
  }

  /**
   * Kare-pipeline'ini kurar: animate() icindeki adimlar fazlara dagitilir.
   * Kayit sirasi = faz icindeki calisma sirasi (or. beforeRender'da once
   * entity.beforeRender, sonra matGeomCache.sync, sonra requests.before).
   *
   * Bu asamada sistemler mevcut private metotlari cagiran ince sarmalayicilardir;
   * sonraki adimlarda her concern ayri bir sistem sinifina tasinacak.
   */
  private _initFramePipeline(): void {
    const p = this._pipeline;

    // sync: sahne her degisince aktif sahneyi track et + scene-mutated yay.
    p.register(this, new SceneMutationSystem());

    // entity yasam dongusu: begin (yeni) -> animate -> beforeRender.
    p.register(this, this._entitySystem);

    // materyal -> geometri onbellegi (entity beforeRender'dan sonra).
    p.register(this, this._matGeomSystem);

    // render oncesi/sonrasi tek seferlik request kuyruklari.
    p.register(this, this._requestSystem);

    // CSS2D/CSS3D once (pending bayragini tuketir), sonra offscreen RT'ler.
    p.register(this, this._cssSystem);
    p.register(this, this._rtSystem);

    // ana WebGL render gecisi (composer / renderer.render).
    p.register(this, this._mainRenderSystem);
  }

  /** Entity yasam dongusu EntitySystem'e devredildi (asagidaki delege yuzeyi). */
  get entities(): Entity[] {
    return this._entitySystem.entities;
  }
  renderer: IRenderer;

  cssRenderer: CSS2DRenderer;
  cssRendererAdded = false;

  cssRenderer3d: CSS3DRenderer;
  cssRenderer3dAdded = false;

  cssRendererVisible = true;

  /**
   * true iken pointer/camera etkilesiminde CSS2D gizlenmez
   * (segment draw sirasinda olcu etiketleri icin).
   */
  css2dVisibleDuringPointer = false;

  get canvas() {
    return this.renderer.instance.domElement;
  }

  get activeScene(): Scene | undefined {
    return this._scene;
  }

  get activeCamera(): CameraInterface | undefined {
    return this._camera;
  }

  set activeCamera(value: CameraInterface) {
    this._camera = value;
    this.updateComposerEffect();
    this.dispatchEvent(new CustomEvent("update-camera"));
  }

  get viewAngle() {
    return this._viewAngle;
  }

  set viewAngle(value) {
    this._viewAngle = value;
  }

  get width() {
    return this._width;
  }

  get height() {
    return this._height;
  }

  get bindedElement() {
    return this._bindElement;
  }

  private _resizeController!: EngineResizeController;

  bindElement = (element: HTMLElement | undefined) => {
    this._bindElement = element;
    this._resizeController.bind(element);
    // Pointer dinleyicileri (etkilesim durumu) InteractionController'da yonetilir.
    this._interaction.bind(element);
  };

  resize = (force = true) => this._resizeController.resize(force);

  /**
   * Verilen kamera ile sahneyi tek seferlik off-cycle render edip blob döner.
   * Aktif kamera / orbit controller'a dokunmaz; composer'ın RenderPass kamerasını
   * geçici olarak swap eder ve işlem sonunda eski haline getirir.
   * Renderer / composer boyutu istenen çözünürlüğe çekilir, sonra restore edilir.
   */
  async renderSnapshot(
    camera: Camera,
    options: { width: number; height: number; type?: string; quality?: number },
  ): Promise<Blob | null> {
    return renderEngineSnapshot(
      {
        renderer: this.renderer,
        composer: this._postProcessing.composer,
        activeScene: this.activeScene!,
        width: this._width,
        height: this._height,
        setSize: (width, height) => {
          this._width = width;
          this._height = height;
        },
        materialGeometryCache: this._materialGeometryCache,
        renderSceneSwap: this._renderSceneSwap,
        setComposerRenderPassScene: (scene) =>
          this._setComposerRenderPassScene(scene),
        requestUpdate: () => {
          this._requestUpdate = true;
        },
      },
      camera,
      options,
    );
  }

  private _systems = new Map();
  addSystem(name: string, object: any) {
    this._systems.set(name, object);
  }

  getSystem(name: string): any {
    return this._systems.get(name);
  }

  /**
   * @param entity
   */
  addEntity = (entity: Entity) => this._entitySystem.add(entity);

  removeEntity = (entity: Entity) => this._entitySystem.remove(entity);

  dispatchCustomEvent(event: string, object?: { detail?: any; event?: Event }) {
    this.dispatchEvent(new CustomEvent(event, object));
  }

  /**
   * Sahneye kucuk bir hata ayiklama mesh'i ekler (kure veya kup).
   * Ornek: Engine.activeEngine?.log("Sphere", worldPosition, worldQuaternion)
   *
   * @param shape "sphere" | "box" | "cube" (buyuk/kucuk harf duyarsiz; "spehere" yazimi da kabul edilir)
   * @param position Dunya konumu
   * @param rotation Istege bagli: Euler, Quaternion veya [x,y,z] (radyan, sira XYZ)
   */
  log(
    shape: string,
    position: Vector3 | readonly [number, number, number],
    rotation?: Euler | Quaternion | readonly [number, number, number],
  ): void {
    this._debugLog.log(shape, position, rotation);
  }

  /** log ile eklenen tum hata ayiklama mesh'lerini ve kaynaklarini kaldirir. */
  clearDebugLog(): void {
    this._debugLog.clear();
  }

  /**
   * MaterialGeometryInvalidationMode bit birlesimi.
   * StructureSignature: mesh / geometry.uuid / material.uuid / visible degisince yenileme.
   * Explicit: ayrica yalnizca invalidateMaterialGeometryCache() ile tam tarama
   * (imza kapaliyken sahne donuk kalir).
   * Varsayilan onbellek modu: StructureSignature.
   */
  setMaterialGeometryInvalidationModes(
    modes: MaterialGeometryInvalidationMode,
  ): void {
    this._materialGeometryCache.setModes(modes);
  }

  getMaterialGeometryInvalidationModes(): MaterialGeometryInvalidationMode {
    return this._materialGeometryCache.getModes();
  }

  /** Sonraki sync tam sahne taramasi yapar. */
  invalidateMaterialGeometryCache(): void {
    this._materialGeometryCache.invalidate();
  }

  /** Son materyal → geometri listesi (referanslar sahne ile paylaşılır). */
  getMaterialGeometryBuckets(): ReadonlyMap<
    Material,
    readonly BufferGeometry[]
  > {
    return this._materialGeometryCache.getBuckets();
  }

  forEachMaterialGeometry(
    callback: (
      material: Material,
      geometries: readonly BufferGeometry[],
    ) => void,
  ): void {
    this._materialGeometryCache.forEachMaterialGeometry(callback);
  }

  /**
   * activeScene icindeki mesh'lerde gecen benzersiz materyaller (sira: uuid).
   * MaterialGeometryCache ile ayni kurallar: Mesh / InstancedMesh, geometrisi olanlar;
   * Line / Points dahil degildir.
   */
  getActiveSceneMaterials(): Material[] {
    return listActiveSceneMaterials(
      this._materialGeometryCache,
      this.activeScene,
    );
  }

  getActiveSceneMaterialsSummary() {
    return summarizeActiveSceneMaterials(
      this._materialGeometryCache,
      this.activeScene,
    );
  }

  logActiveSceneMaterialsToConsole(label = "activeScene materials"): void {
    logActiveSceneMaterialsToConsoleImpl(
      this._materialGeometryCache,
      this.activeScene,
      label,
    );
  }

  /**
   * true iken her request-update onbellegi stale isaretler;
   * boylece sonraki karede modlara gore yeniden taranir.
   */
  setMaterialGeometryCacheListenRequestUpdate(bind: boolean): void {
    if (bind === this._materialGeometryCacheRequestUpdateListening) return;
    if (bind) {
      this.addEventListener(
        "request-update",
        this._materialGeometryCacheOnRequestUpdate as EventListener,
      );
    } else {
      this.removeEventListener(
        "request-update",
        this._materialGeometryCacheOnRequestUpdate as EventListener,
      );
    }
    this._materialGeometryCacheRequestUpdateListening = bind;
  }

  /**
   * true iken WebGL her karede mesh'leri materyal gruplarina reparent edip
   * activeSceneToRender uzerinden cizer; kare sonunda activeScene hiyerarsisine geri alir.
   * CSS2D/CSS3D activeScene uzerinde kalir (_renderTargets).
   */
  setUseRenderSceneSwap(value: boolean): void {
    this._renderSceneSwap.setEnabled(value, this._scene, (s) =>
      this._setComposerRenderPassScene(s),
    );
    this.dispatchCustomEvent("webgl-render-scene-changed");
  }

  get useRenderSceneSwap(): boolean {
    return this._renderSceneSwap.enabled;
  }

  /**
   * Swap aciken materyal basina birlesik geometri -> tek draw call (CPU'da vertex kopyasi).
   * Animasyonlu donusumler imza yenilenmedikce guncellenmez; statik urun / konfiguratur icin uygundur.
   */
  setRenderSceneMergeMeshesPerMaterial(value: boolean): void {
    this._renderSceneSwap.setMergeMeshesPerMaterial(value);
  }

  get renderSceneMergeMeshesPerMaterial(): boolean {
    return this._renderSceneSwap.mergeMeshesPerMaterial;
  }

  /**
   * setUseRenderSceneSwap(true) iken activeSceneToRender icerigini ve bucket'lari konsola yazar.
   * @param logEachFrame her kare -- cok gurultulu.
   */
  setDebugLogRenderScene(enabled: boolean, logEachFrame = false): void {
    this._renderSceneSwap.setDebugLog(enabled, logEachFrame);
  }

  /**
   * Per-frame profiler. Açıkken her saniye özet tablo basar,
   * slowFrameMs'i aşan frame'lerde breakdown uyarısı atar.
   *
   * Konsoldan: window.__frameProfiler.setEnabled(true)
   * Parametrik config: window.__engineProfiling.setConfig({ perEntity: false, ... })
   */
  setFrameProfilerEnabled(
    enabled: boolean,
    options?: { slowFrameMs?: number; flushEveryMs?: number },
  ): void {
    if (options?.slowFrameMs != null) {
      frameProfiler.setSlowFrameThreshold(options.slowFrameMs);
    }
    if (options?.flushEveryMs != null) {
      frameProfiler.setFlushInterval(options.flushEveryMs);
    }
    frameProfiler.setEnabled(enabled);
  }

  get debugLogRenderScene(): boolean {
    return this._renderSceneSwap.debugLog;
  }

  get debugLogRenderSceneEachFrame(): boolean {
    return this._renderSceneSwap.debugEachFrame;
  }

  logRenderSceneStateNow(reason = "manual"): void {
    if (this.activeScene) {
      this._materialGeometryCache.sync(this.activeScene);
    }
    this._renderSceneSwap.logStateNow(
      this.activeScene,
      this._materialGeometryCache.getLastStructureSignature(),
      this._materialGeometryCache.getBuckets(),
      reason,
    );
  }

  get activeSceneToRender(): Scene {
    return this._renderSceneSwap.sceneToRender;
  }

  getSceneForWebGLRender(): Scene {
    return this._renderSceneSwap.getWebGLScene(this._scene!);
  }

  private _setComposerRenderPassScene(target: Scene): void {
    this._postProcessing.setRenderPassScene(target);
  }

  /**
   * Ana WebGL render geçişini tamamen değiştirir. Set edildiğinde Engine,
   * render fazında EffectComposer / renderer.render yolunu çalıştırmaz; yalnızca
   * verilen callback'i çağırır. CSS2D/CSS3D render ve entity animate akışı
   * etkilenmez. Başka bir pipeline devralmak için kullanılır
   * (ör. FakeLumenPipelineElement).
   */
  setRenderOverride(fn: ((engine: Engine) => void) | undefined): void {
    this._mainRenderSystem.setRenderOverride(fn);
    this.dispatchCustomEvent("request-update");
  }

  get hasRenderOverride(): boolean {
    return this._mainRenderSystem.hasRenderOverride;
  }

  /**
   *
   * @param renderTarget
   */
  addRenderTarget(renderTarget: RenderTarget) {
    this._rtSystem.add(renderTarget);
  }

  removeRenderTarget(renderTarget: RenderTarget | undefined) {
    this._rtSystem.remove(renderTarget);
  }

  /**
   * pass composer part — yonetim PostProcessingController'a tasindi.
   * Asagidaki metotlar yalnizca delege eder (geriye uyumlu yuzey).
   */
  mutatePassComposer(mutate: (composer: EffectComposer) => void): void {
    this._postProcessing.mutate(mutate);
  }

  mutatePassComposerIfExists(mutate: (composer: EffectComposer) => void): void {
    this._postProcessing.mutateIfExists(mutate);
  }

  addPass(pass?: Pass) {
    this._postProcessing.addPass(pass);
  }

  removePass(pass?: Pass) {
    this._postProcessing.removePass(pass);
  }

  hasPass(pass?: Pass) {
    return this._postProcessing.hasPass(pass);
  }

  /** Generic WebGPU TSL effect (AO/outline/TAA bilmez — effect nesnesi elementte). */
  addEffect(effect: TslEffect) {
    this._postProcessing.addEffect(effect);
  }

  removeEffect(effect: TslEffect) {
    this._postProcessing.removeEffect(effect);
  }

  findEffect<T extends TslEffect>(
    predicate: (effect: TslEffect) => effect is T,
  ): T | undefined {
    return this._postProcessing.findEffect(predicate);
  }

  /** Force WebGPU TSL post graph rebuild. */
  invalidateTslEffects(): void {
    this._postProcessing.invalidateTslEffects();
  }

  private updateComposerEffect() {
    this._postProcessing.updateCameraScene();
  }

  /** lil-gui debug paneli — EngineGuiController'a delege. */
  async addGUIFolder(name: string) {
    return this._gui.addFolder(name);
  }

  private _paused = false;
  private _rafScheduled = false;
  private _rafScheduleCount = 0;
  private _animateBoundForRAF = (t: number) => {
    this._rafScheduled = false;
    this.animate(t);
  };
  private _scheduleNextFrame(): void {
    if (this._rafScheduled) return;
    this._rafScheduled = true;
    this._rafScheduleCount++;
    requestAnimationFrame(this._animateBoundForRAF);
  }
  pause() {
    this._paused = true;
  }
  resume() {
    this._paused = false;
    this._scheduleNextFrame();
  }
  isPaused() {
    return this._paused;
  }
  animate(dt: number) {
    if (this._paused) return;
    this._scheduleNextFrame();
    this._resizeController.discardPresentedFreeze();
    this._resizeController.applyPendingResize();

    frameProfiler.beginFrame();

    const ctx: FrameContext = { engine: this, dt };

    // sync..renderTargets fazlari tek blokta; structural gauge bunlardan sonra,
    // render fazindan once okunmali (eski animate sirasiyla birebir).
    this._pipeline.run(ctx, [
      "sync",
      "begin",
      "update",
      "beforeRender",
      "renderTargets",
    ]);

    engineProfiling.captureStructuralGauges({
      entitiesCount: this.entities.length,
      passesCount: this._postProcessing.composer?.passes.length ?? 0,
      renderTargetsCount: this._rtSystem.count,
      rafScheduledTotal: this._rafScheduleCount,
      scene: this.activeScene ?? undefined,
    });

    this._pipeline.run(ctx, ["render"]);
    engineProfiling.captureGlGauges(this.renderer.instance);

    this._pipeline.run(ctx, ["afterRender"]);

    frameProfiler.endFrame();
  }

  /**
   * Son ana sahne render geçişinin toplam draw call ve üçgen sayısı (RT geçişleri dahil değil).
   * addAfterRequest / engine-request-after ile kayitli callback'lerde okunmali (afterRender sirasi).
   */
  getLastWebGLMainFrameStats(): {
    calls: number;
    triangles: number;
  } | null {
    return this._mainRenderSystem.getLastWebGLMainFrameStats();
  }

  addBeforeRequest(req: EngineRequest) {
    this._requestSystem.addBefore(req);
  }

  addAfterRequest(req: EngineRequest) {
    this._requestSystem.addAfter(req);
  }

  /**
   * Waits for WebGPURenderer.init() and KTX2 detectSupport.
   * Call before start() / first texture load.
   */
  async ensureReady(): Promise<void> {
    await this.renderer.ensureReady();
    const { KTX2LoaderSingleton } = await import("../loader/KTX2LoaderSingleton");
    await KTX2LoaderSingleton.ensureSupportDetected();
  }

  start() {
    if (this._start) {
      logger.warn(
        "Engine.start() called multiple times — ignoring duplicate (prevents rAF loop leak)",
      );
      return;
    }
    this._start = true;
    void this.renderer.ensureReady().then(() => {
      if (!this._start) return;
      this.animate(0);
    });
  }

  /**
   * @internal CssRenderSystem icin: bekleyen guncelleme bayragi (CSS render gate).
   * Bayrak resize / request-update / render-override / snapshot gibi cok yerden
   * set edilir; bu yuzden Engine'de kalir, sistem yalnizca okur/tuketir.
   */
  get pendingRequestUpdate(): boolean {
    return this._requestUpdate;
  }

  /** @internal CssRenderSystem kare basina bir kez bayragi tuketir. */
  clearRequestUpdate(): void {
    this._requestUpdate = false;
  }
}
